/**
 * Inline SVG icon set. Never emoji, per the art-direction rules.
 *
 * These are the *structural* icons (navigation, resources, affordances) that must stay
 * crisp at 12px and recolour with `currentColor`. Illustrated emblems -- crests, badges,
 * mission icons, map markers -- come from the generated vector library instead.
 */

export type IconName = keyof typeof P

const P = {
  home: 'M3 11.2 12 4l9 7.2V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  bell: 'M6 16.5V11a6 6 0 0 1 12 0v5.5l1.6 2H4.4zM10 21h4',
  jobs: 'M4 7h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1zm5 0V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M3 12h18',
  crimes: 'M12 3c-4.4 0-8 3.1-8 7 0 2.4 1.3 4.5 3.3 5.8L6 21l3.4-1.6c.8.2 1.7.3 2.6.3s1.8-.1 2.6-.3L18 21l-1.3-5.2c2-1.3 3.3-3.4 3.3-5.8 0-3.9-3.6-7-8-7zm-3 8.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm6 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z',
  families: 'M8.5 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm7 .5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zM2.5 20v-1.6c0-2.4 2.7-4.4 6-4.4s6 2 6 4.4V20zm14 0v-1.6c0-1.3-.5-2.5-1.4-3.4 2.9.2 5.4 2 5.4 4.1V20z',
  market: 'M4 4h2l2.2 10.4a1 1 0 0 0 1 .8h8.1a1 1 0 0 0 1-.8L20 8H7M9.5 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm8 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  territory: 'M3 6.5 9 4l6 2.5L21 4v13.5L15 20l-6-2.5L3 20zM9 4v13.5M15 6.5V20',
  contracts: 'M6 3h9l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm8 .5V7h3.5M8.5 12h7M8.5 15.5h7M8.5 8.5h3',
  hitlist: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 4.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zm0 3a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM12 1v3M12 20v3M1 12h3M20 12h3',
  scanner: 'M12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM8.4 8.4a5 5 0 0 0 0 7.2M15.6 8.4a5 5 0 0 1 0 7.2M5.6 5.6a9 9 0 0 0 0 12.8M18.4 5.6a9 9 0 0 1 0 12.8',
  ledger: 'M4 4.5A1.5 1.5 0 0 1 5.5 3H19a1 1 0 0 1 1 1v14.5a1 1 0 0 1-1 1H5.5A1.5 1.5 0 0 1 4 18zm0 13A1.5 1.5 0 0 1 5.5 16H20M8 7h8M8 10.5h5',

  energy: 'M13 2 4.5 13.5H11L10 22l8.5-11.5H12z',
  nerve: 'M12 2 14.6 8.2 21 9l-4.6 4.6L17.6 20 12 16.8 6.4 20l1.2-6.4L3 9l6.4-.8z',
  dirty: 'M2.5 7h19a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-19a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1zm9.5 2.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM5 9.5h.01M19 14.5h.01',
  clean: 'M12 3 3 7.5V9h18V7.5zM5 11v7M9.5 11v7M14.5 11v7M19 11v7M2.5 20h19',
  heat: 'M12 2c1.5 4 5.5 5.4 5.5 10a5.5 5.5 0 0 1-11 0c0-1.6.6-2.8 1.4-3.9.3 1.2 1 2 1.9 2.3C9.4 7.6 10.7 4.6 12 2z',
  legend: 'M12 2 4 5.5v6c0 5 3.4 9.3 8 10.5 4.6-1.2 8-5.5 8-10.5v-6zm0 5.5 1.5 3.2 3.5.5-2.5 2.5.6 3.5-3.1-1.7-3.1 1.7.6-3.5L7 11.2l3.5-.5z',
  rank: 'M4 8.5 12 4l8 4.5M4 13l8 4.5L20 13M4 17.5 12 22l8-4.5',
  rep: 'M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9z',

  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 4v5.2l3.6 2.1',
  chev: 'M9 5.5 15.5 12 9 18.5',
  chevd: 'M5.5 9 12 15.5 18.5 9',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  close: 'M6 6l12 12M18 6 6 18',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zm5 12 4.5 4.5',
  filter: 'M3 5h18l-7 8v6l-4 2v-8z',
  lock: 'M7 10V7.5a5 5 0 0 1 10 0V10M5 10h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1z',
  check: 'M5 12.5 10 17.5 19.5 7',
  warn: 'M12 3 22 20H2zM12 9.5v5M12 17h.01',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 4.5h.01M12 11v6',
  send: 'M3.5 12 21 4l-8 17-2.5-7z',
  refresh: 'M20 5.5v5h-5M4 18.5v-5h5M19.2 13a7.5 7.5 0 0 1-13.4 3M4.8 11a7.5 7.5 0 0 1 13.4-3',
  target: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm0 5a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 11.2a.8.8 0 1 0 0 1.6.8.8 0 0 0 0-1.6z',
  pin: 'M12 2a6.5 6.5 0 0 0-6.5 6.5C5.5 14 12 22 12 22s6.5-8 6.5-13.5A6.5 6.5 0 0 0 12 2zm0 4.2a2.3 2.3 0 1 1 0 4.6 2.3 2.3 0 0 1 0-4.6z',
  mail: 'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1zm0 1.5 9 6 9-6',
  gear: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm8.5 3a8.5 8.5 0 0 0-.15-1.5l2-1.5-2-3.4-2.35 1a8.5 8.5 0 0 0-2.6-1.5L15 2.5H9l-.4 2.6a8.5 8.5 0 0 0-2.6 1.5l-2.35-1-2 3.4 2 1.5a8.5 8.5 0 0 0 0 3l-2 1.5 2 3.4 2.35-1a8.5 8.5 0 0 0 2.6 1.5L9 21.5h6l.4-2.6a8.5 8.5 0 0 0 2.6-1.5l2.35 1 2-3.4-2-1.5c.1-.5.15-1 .15-1.5z',
  trophy: 'M7 4h10v5a5 5 0 0 1-10 0zM7 5.5H4V8a3 3 0 0 0 3 3M17 5.5h3V8a3 3 0 0 1-3 3M9.5 14h5l.5 5h-6zM7.5 19h9v2h-9z',
  help: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zm-2.2 6.6a2.2 2.2 0 1 1 3 2.1c-.5.2-.8.7-.8 1.3v.5M12 17h.01',
  rain: 'M7 15.5A4 4 0 0 1 7.6 7.6a5.5 5.5 0 0 1 10.6 1.6 3.5 3.5 0 0 1-.7 6.9M8.5 18l-1 3M12 18l-1 3M15.5 18l-1 3',
  fist: 'M7 10V6.5a1.5 1.5 0 0 1 3 0V10m0 0V5.5a1.5 1.5 0 0 1 3 0V10m0 0V6.5a1.5 1.5 0 0 1 3 0V13c0 4-2.5 7-6 7s-6-3-6-7v-2.5a1.5 1.5 0 0 1 3 0z',
  cross: 'M10 3h4v5h5v4h-5v5h-4v-5H5V8h5z',
  skull: 'M12 3a8 8 0 0 0-8 8c0 2.6 1.3 4.6 3 5.7V20h10v-3.3c1.7-1.1 3-3.1 3-5.7a8 8 0 0 0-8-8zm-3 8a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4zm6 0a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4z',
  crown: 'M3 8l3.5 3L12 5l5.5 6L21 8v9H3z',
  crate: 'M3 6h18v12H3zM3 6l3 12M21 6l-3 12M3 10h18M3 14h18',
  zoomin: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zm5 12 4.5 4.5M8 11h6M11 8v6',
  zoomout: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zm5 12 4.5 4.5M8 11h6',
  center: 'M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM12 2v3M12 19v3M2 12h3M19 12h3',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zm-7 9v-1.5C2 16 5.1 14 9 14s7 2 7 4.5V20zm15-9a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm5 9v-1.5c0-1.9-1.7-3.5-4-4.2 1.9.6 3 1.9 3 3.2V20z',
  eye: 'M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12zm10-3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
  bolt: 'M13 2 4.5 13.5H11L10 22l8.5-11.5H12z',
} as const

interface Props {
  n: IconName
  s?: number
  c?: string
  fill?: boolean
  className?: string
  style?: React.CSSProperties
}

export function Icon({ n, s = 16, c = 'currentColor', fill, className, style }: Props) {
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      className={className}
      style={{ flex: 'none', display: 'block', ...style }}
      fill={fill ? c : 'none'}
      stroke={fill ? 'none' : c}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d={P[n]} />
    </svg>
  )
}

/** Resource glyph in its semantic colour, used across the header and every cost readout. */
export function Res({ k, s = 13 }: { k: 'energy' | 'nerve' | 'dirty' | 'clean' | 'heat' | 'legend' | 'rep' | 'rank'; s?: number }) {
  const c: Record<string, string> = {
    energy: 'var(--energy)',
    nerve: 'var(--nerve)',
    dirty: 'var(--dirty)',
    clean: 'var(--clean)',
    heat: 'var(--heat)',
    legend: 'var(--gold)',
    rep: 'var(--gold)',
    rank: 'var(--brass-lit)',
  }
  return <Icon n={k as IconName} s={s} c={c[k]} fill={k === 'heat' || k === 'energy'} />
}
